/**
 * wb-plp-atc.js — « Ajouter au panier » depuis une carte de PLP en mode liste.
 * ===========================================================================
 *
 * Posé le 07/09/2026. Le mode liste porte deux boutons : « Voir le produit »
 * (un simple lien) et « Ajouter au panier », qui est tout l'objet de ce fichier.
 *
 * ── Pourquoi la variante est résolue AU CLIC ────────────────────────────────
 * `/cart/add.js` n'accepte qu'un identifiant de VARIANTE. Or l'index de parité
 * Typesense (28 champs, `typesense/schemas/parity_schema.py`) n'en porte aucun :
 * il a `id` (le produit), `sku`, `barcode`, `options`, `handle` — pas de
 * `variant_id`. L'y ajouter demanderait une reconstruction complète des sept
 * langues, sur la VM. Arbitrage du 07/09 : on résout au clic.
 *
 * Le clic lit donc `<url du produit>.js`, le point JSON natif de Shopify, qui
 * rend le produit et ses variantes. C'est une requête par clic, servie par le
 * CDN et mise en cache par le navigateur — pas par produit affiché.
 *
 * ⛔ L'URL interrogée est celle du LIEN DE LA CARTE, pas un chemin reconstruit.
 * C'est ce qui rend le mécanisme insensible au préfixe de locale : sur le marché
 * France de la production les URLs vivent sous `/fr-fr/`, et une racine en dur
 * sort de la locale (piège du 28/07/2026, corrigé alors via `Shopify.routes.root`).
 * Ici le href porte déjà le bon préfixe, quel que soit l'hôte.
 *
 * ── Produits à plusieurs variantes ─────────────────────────────────────────
 * Ils ne vont PAS au panier : le bouton ouvre la fiche. Ajouter d'office la
 * première variante d'une guitare déclinée en trois finitions mettrait au panier
 * autre chose que ce que le visiteur croit choisir. Le libellé du bouton est
 * réécrit après résolution (« Choisir les options ») pour que le second clic dise
 * la vérité.
 *
 * ── Ce qu'on NE réimplémente pas ───────────────────────────────────────────
 * Le rafraîchissement du panier. `assets/cart.js` écoute `cart:refresh` sur
 * `document` et fait tout : il refetche le HTML du tiroir ET `/cart.js`, publie
 * `PUB_SUB_EVENTS.cartUpdate` (d'où la mise à jour du compteur d'en-tête), puis
 * ouvre le tiroir si `detail.open` est vrai. On se contente donc de POSTer puis
 * d'émettre cet événement. ⛔ Ne pas republier `cartUpdate` soi-même : sans les
 * `sections` du tiroir, la charge est incomplète et le tiroir se vide.
 *
 * ── Délégation, et pourquoi ────────────────────────────────────────────────
 * Un seul écouteur sur `document`. Les grilles de PLP sont REPEINTES côté client
 * — par Globo en production, par le front Typesense en preprod — donc tout
 * écouteur posé sur un bouton disparaît au premier re-rendu, sans erreur. Même
 * leçon que la réécriture des URLs de cartes du 02/09.
 */
(function () {
  "use strict";

  var SEL_BOUTON = "[data-wb-atc]";
  var CLASSE_CHARGEMENT = "btn--loading";

  /* Cache des résolutions, par URL de produit. Deux clics sur la même carte, ou
     un clic sur deux cartes du même produit, ne relisent pas le JSON. */
  var resolus = Object.create(null);

  /** Le point JSON natif du produit, dérivé du href du bouton. */
  function urlJson(href) {
    var u = new URL(href, window.location.origin);
    return u.origin + u.pathname.replace(/\/+$/, "") + ".js";
  }

  /** Libellé de repli : les data-* du bouton portent les traductions. */
  function libelle(bouton, cle, defaut) {
    return bouton.getAttribute("data-wb-label-" + cle) || defaut;
  }

  function texteDu(bouton) {
    return bouton.querySelector(".btn__text") || bouton;
  }

  /** Les routes du thème, avec repli : `FoxTheme` est chargé en `defer`. */
  function routeAjout() {
    var r = window.FoxTheme && window.FoxTheme.routes;
    return (r && r.cart_add_url) || "/cart/add";
  }

  function config() {
    var u = window.FoxTheme && window.FoxTheme.utils;
    if (u && u.fetchConfig) return u.fetchConfig("javascript");
    return {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/javascript",
      },
    };
  }

  /**
   * Résout le produit du bouton. Rend `{ variantId }` s'il n'a qu'une variante
   * disponible, `{ url }` s'il faut ouvrir la fiche, `{ indisponible: true }`
   * s'il est en rupture.
   */
  function resoudre(href) {
    if (resolus[href]) return resolus[href];

    var promesse = fetch(urlJson(href), {
      headers: { Accept: "application/json" },
    })
      .then(function (r) {
        if (!r.ok) throw new Error("HTTP " + r.status);
        return r.json();
      })
      .then(function (produit) {
        var variantes = (produit && produit.variants) || [];
        if (variantes.length !== 1) return { url: href };

        var v = variantes[0];
        if (v.available === false) return { indisponible: true };
        return { variantId: v.id };
      });

    resolus[href] = promesse;
    /* ⛔ Un échec ne doit pas rester en cache : sinon une coupure réseau
       condamne le bouton pour toute la durée de la page. */
    promesse.catch(function () {
      delete resolus[href];
    });
    return promesse;
  }

  function ajouter(variantId) {
    var c = config();
    c.body = JSON.stringify({ items: [{ id: variantId, quantity: 1 }] });
    return fetch(routeAjout() + ".js", c)
      .then(function (r) {
        return r.json();
      })
      .then(function (etat) {
        /* `/cart/add.js` répond 422 avec un `status` sur rupture ou règle de
           quantité — un `r.ok` seul ne suffit pas à conclure. */
        if (etat && etat.status) throw new Error(etat.description || etat.message);
        return etat;
      });
  }

  function rafraichirPanier() {
    document.dispatchEvent(
      new CustomEvent("cart:refresh", { bubbles: true, detail: { open: true } })
    );
  }

  function surClic(evenement) {
    var bouton = evenement.target.closest && evenement.target.closest(SEL_BOUTON);
    if (!bouton || bouton.hasAttribute("aria-disabled")) return;

    var href = bouton.getAttribute("href");
    if (!href) return;

    /* Le bouton est une ancre vers la fiche : c'est le repli sans JS, et il doit
       le rester. On ne prend la main qu'à partir d'ici. */
    evenement.preventDefault();

    var texte = texteDu(bouton);
    var libelleInitial = texte.textContent;
    bouton.setAttribute("aria-disabled", "true");
    bouton.classList.add(CLASSE_CHARGEMENT);

    resoudre(href)
      .then(function (issue) {
        if (issue.url) {
          /* Plusieurs variantes : on le dit, puis on ouvre la fiche. */
          texte.textContent = libelle(bouton, "options", libelleInitial);
          window.location.href = issue.url;
          return null;
        }
        if (issue.indisponible) {
          texte.textContent = libelle(bouton, "indisponible", libelleInitial);
          return null;
        }
        return ajouter(issue.variantId).then(rafraichirPanier);
      })
      .catch(function (erreur) {
        console.warn("[wb-plp-atc] ajout au panier impossible :", erreur.message);
        /* Repli honnête : on envoie sur la fiche, où l'ajout marche toujours. */
        window.location.href = href;
      })
      .finally(function () {
        bouton.classList.remove(CLASSE_CHARGEMENT);
        bouton.removeAttribute("aria-disabled");
        if (texte.textContent === libelleInitial) return;
        /* Le libellé « indisponible » reste ; les autres cas ont navigué. */
      });
  }

  document.addEventListener("click", surClic);
})();
